/**
 * Ein ganz bewußt nicht Promise/A+ - konformer Wrapper für ES6-Promises, um vernünftiges Error-Handling zu ermöglichen.
 *
 * ES6-Promises haben leider eine Reihe von fundamentalen Problemen:
 *
 * 1. Wenn man keinen catch am Ende der then - Kette hat, werden Exceptions verschluckt. Aber selbst wenn man immer an den
 *    letzten catch denkt - darin geworfene Fehler werden natürlich ... verschluckt.
 *
 * 2. Verschiedene Browser-Implementierungen gehen unterschiedlich mit nicht abgefangenen Fehlern innerhalb von
 *    then/catch um: manche verschlucken sie, andere loggen sie wenigstens, andere werfen sie weiter, einige
 *    analysieren den Code... halleluja!
 *
 * 3. Im Debugger landet man bei "Pause on Exceptions" nicht an der Fehlerstelle des then,
 *    sondern an der rethrow-Stelle im catch. Man kommt dann nur über Stacktraces/Logging oder explizite Breakpoints weiter.
 *
 * SafePromise stellt einen Versuch zur Abhilfe dar:
 *
 * 1. Man kann das finale catch weglassen, wenn man nicht auf den Fehler reagieren möchte. Der Fehler wird dann
 *    an die Standard-Fehlerbehandlung übergeben, knallt also hoch bis zu window.onerror.
 *    Debugging ist dadurch wie gewohnt möglich.
 *
 * 2. Man kann eine zusätzliche Default-Fehlerbehandlung angeben, die ausschließlich für den Anfang des Promise aktiv wird,
 *    wenn kein späterer catch angegeben wurde. Die Default-Fehlerbehandlung kann überschrieben werden.
 *    Das ist insbesondere für den häufigen Fall von AJAX-Calls praktisch.
 *
 * <h4>Von Versprechen und warum man sie brechen sollte</h4>
 *
 * Ein echtes Promise, so wie es die Hohepriester der Informatik ersonnen haben, gilt bis in alle Ewigkeiten
 * oder bis der Shutdown des Browsertabs die Bits und Bytes scheidet.
 * Das ist ein sehr simples Konzept, was jeder versteht, und ist für den Erwartungsfall meist kein Problem -
 * das Promise konserviert und kapselt seinen Zustand halt weg -, kehrt aber den ganzen Dreck, die Ausnahmen,
 * einfach unter den Teppich.
 * FailFast zu sein ist nämlich auch ein Wert an sich, und wird dabei einfach ignoriert.
 * Das kann man mit so einem mächtigen Versprechen und ohne die Vorsehung statischer Codeanalyse auch gar nicht
 * anders machen, denn Promises können nicht wissen, ob und wie sie jemals eingelöst werden, und warten
 * daher brav auf die Erlösung durch ein Catch oder Then. Nur tritt das  womöglich niemals ein...
 * Das ist der wesentliche Unterschied zu den try/catch - Codeblöcken im synchronen Fall,
 * die als Metapher für das Verständnis von Promises herangezogen werden.
 * Um das normale Exception-Handling überhaupt wieder zu ermöglichen, brechen wir kurz, schmerzlos und
 * überaus dreckig direkt zu Anfang das Ewigkeitsversprechen des Promise. Wir legen uns auf einen genauen
 * Zeitpunkt fest, an dem das Promise ablaufen wird. Wenn man es bis dahin nicht geschafft hat, seine
 * thens und catchs dranzuhängen - Pech. 10 ms scheinen mir dafür mehr als ausreichend zu sein, da der typische
 * Code im Umgang mit Promises so aussieht, dass die thens und catches in Form einer statischen Kette
 * gleich zu Anfang festgelegt werden und der Promise sich dann schlafen legt, bis das asynchrone Ereignis
 * eintritt. Wir verzögern hier aber in jedem Fall, also auch wenn das asynchrone Ereignis eher eintritt.
 * 10 ms sollten aber schnell genug sein, so dass die APP mit dieser Verzögerung kein Problem hat.
 * Man kann die 10 ms im Zweifel auch ändern (waitTimeMs), oder man benutzt dann eben doch normale Promises,
 * wenn man einen anderen Anwendungsfall hat.
 *
 * Siehe z.B. diese Diskussion:
 * https://www.reddit.com/r/javascript/comments/4bj6sm/am_i_wrong_to_be_annoyed_with_promise_error
 *
 * @example
 * function meineApiFunction() {
 *    return new SafePromise($.getJson('http://test.test')).defaultCatch(serverCommunicationError);
 * }
 *
 * // jetzt wird bei einem AJAX-Fehler der defaultCatch aus meineApiFunction aufgerufen
 * meineApiFunction().then(()=>console.log('juhu'));
 *
 * // jetzt wird bei einem AJAX-Fehler der defaultCatch nicht aufgerufen, statt dessen möp! ausgegeben
 * meineApiFunction().then(()=>console.log('juhu')).catch(()=>console.log('möp!'));
 *
 * // falls kein AJAX-Fehler passiert, knallt es aufgrund des throw "Ups!" im ersten then, aber die Exception
 * // wird nicht wie bei Promises üblich aufgrund des fehlenden catch verschluckt, sondern ganz normal bis auf
 * // die oberste Ebene weitergeworfen und vom window.onerror - Handler verarbeitet
 * meineApiFunction().then(()=>{throw "Ups!";});
 *
 * // jetzt wird bei einem AJAX-Fehler der hier definierte defaultCatch verwendet, also Bingo! ausgegeben
 * meineApiFunction().defaultCatch((reason)=>console.log('Bingo!'));
 */
export default class SafePromise {

    /**
     * @param delegate das "echte" Promise
     * @param waitTimeMs das Verfallsdatum des Promise, collapse of the wave function
     */
    constructor(delegate, waitTimeMs = 10) {
        if (delegate==null || delegate.then==undefined) {
            throw "delegate must be a Promise";
        }
        this.redeemed = false;
        this.list = [];
        this._defaultCatch = null;
        this.constructorStack = new Error().stack;
        const breakPromise = (asThen) => (argument) => setTimeout(()=>this._iterate(asThen,argument),waitTimeMs);
        delegate.then(breakPromise(true),breakPromise(false));
    }

    /**
     * wie promise.then
     * @param {Function} resolve
     * @param {?Function} reject
     */
    then(resolve, reject) {
        this._checkRedeemed();
        this._push(true,resolve);
        if (_.isFunction(reject)) {
            this._push(false,reject);
        }
        return this;
    }

    /** */
    done() {
        throw "use SafePromise.then instead of done";
    }

    /**
     * wie promise.catch
     * @param {Function} reject
     */
    catch(reject) {
        this._checkRedeemed();
        this._push(false,reject);
        return this;
    }

    /**
     * dieser defaultCatch wird nur aktiv, wenn das Promise direkt am Anfang fehl schlägt und wenn kein folgender
     * normaler catch existiert. Es gibt nur einen einzigen möglichen DefaultCatch, d.h. durch nachträgliche Aufrufe
     * dieser Methode überschreibt man den eventuell zuvor gesetzten DefaultCatch.
     * @param {Function} reject
     */
    defaultCatch(reject) {
        this._checkRedeemed();
        this._defaultCatch = reject;
        return this;
    }

    /**
     * @private
     */
    _checkRedeemed() {
        if (this.redeemed) {
            throw new Error('SafePromise was allready redeemed so no further then or catch would be executed');
        }
    }

    /**
     * @private
     * @param {boolean} isThen
     * @param {Function} perform
     */
    _push(isThen, perform) {
        this.list.push({isThen,perform});
    }

    /**
     * @private
     */
    _iterate(asThen,argument) {
        this.redeemed = true;
        const followingCatch = Array(this.list.length);
        let hadCatch = false;
        for (let i=followingCatch.length-1;i>=0;i--) {
            followingCatch[i] = hadCatch;
            hadCatch = hadCatch || !this.list[i].isThen;
        }
        if (!asThen && !followingCatch[0] && this._defaultCatch) {
            if (_.isString(this.constructorStack)) {
                try {
                    this._defaultCatch(argument);
                } catch (e) {
                    e = this._restoreStack(e);
                    console.error(e);
                    // wenn der Debugger hier landet - zuvor geloggten Stacktrace in der Console anschauen,
                    // da kommt man dann bei der ersten oder zweiten Codezeile schon zur richtigen Stelle
                    throw e;
                }
            } else {
                this._defaultCatch(argument);
            }
            return;
        }
        this._iterate2(0,followingCatch,asThen,argument);
    }

    _iterate2(i,followingCatch,asThen,argument) {
        if (i>=this.list.length) {
            if (!asThen) {
                throw argument;
            }
            return;
        }
        const next = this.list[i];
        if (asThen!=next.isThen) {
            this._iterate2(i+1,followingCatch,asThen,argument);
            return;
        }
        if (followingCatch[i]) {
            // es gibt ein späteres catch, also werden Fehler abgefangen und an das spätere catch weitergeleitet
            try {
                argument = next.perform(argument);
                asThen = true;
            } catch (e) {
                argument = e;
                asThen = false;
            }
        } else {
            // es gibt kein späteres catch. Fehler werden also nicht abgefangen,
            // so dass der Debugger direkt an den Ort des Fehlers springen kann
            argument = next.perform(argument);
        }

        if (argument instanceof Promise || argument instanceof SafePromise) {
            // Falls die Rückgabe selbst ein Promise ist, muss dieser aufgelöst und das Resultat weitergegeben werden
            argument.then((result)=>{
                argument = result;
                asThen = true;
                this._iterate2(i+1,followingCatch,asThen,argument);
            },(reason)=>{
                argument = reason;
                asThen = false;
                this._iterate2(i+1,followingCatch,asThen,argument);
            });
        } else {
            this._iterate2(i+1,followingCatch,asThen,argument);
        }
    }

    /**
     * Setzt den Stacktrace auf den ursprünglichen Ort des Geschehens.
     * Das ist zumindest für AJAX-Promises ein guter Workaround, weil wir da leider den Debugger
     * nicht an die richtige Stelle setzen können
     * @private
     */
    _restoreStack(e) {
        let betterStack = this.constructorStack.split('\n').slice(2);
        if (e instanceof Error) {
            const newStack = e.stack.split('\n').slice(0,1).concat(...betterStack);
            e.stack = e+'\n'+newStack.join('\n');
        } else if (e.status && e.statusText) {
            const newError = new Error(e.statusText + ' (' + e.status+') '+e.responseText);
            newError.stack = betterStack;
            return newError;
        }
        return e;
    }

}
